/**
 * EN-1492: copy for the keep-or-discard edits modal and the single-field regenerate confirm, verbatim from
 * the approved design (OptionA3 COPY, spec copy table, D14).
 */
import { toast } from "react-toastify";
import type { EditRowTag } from "@/common/lib/api/documentEdits";

export type KeepEditsTrigger = "newRecording" | "retry" | "generateSeveral" | "regenerateSection";

export const TRIGGER_COPY: Record<
    KeepEditsTrigger,
    { subheader: string; keep: string; discard: string; confirm: string }
> = {
    newRecording: {
        subheader: "You added a new recording. What should happen to your edits?",
        keep: "Fields you edited stay exactly as you wrote them. Everything else uses the new recording.",
        discard: "Rewrite everything from every recording, including the new one.",
        confirm: "Generate",
    },
    retry: {
        subheader: "You are regenerating a document you have edited. What should happen to your edits?",
        keep: "Fields you edited stay exactly as you wrote them. Everything else is rewritten.",
        discard: "Rewrite the whole document. Your edited version stays in version history.",
        confirm: "Retry generation",
    },
    generateSeveral: {
        subheader: "Some of the documents you are generating have your edits. What should happen to them?",
        keep: "Fields you edited stay exactly as you wrote them. Everything else is rewritten.",
        discard: "Rewrite every document. Your edited versions stay in version history.",
        confirm: "Generate",
    },
    regenerateSection: {
        subheader: "You are regenerating <section>, which has your edits. What should happen to them?",
        keep: "Fields you edited stay exactly as you wrote them. The rest of the section is rewritten.",
        discard: "Rewrite the whole section. Your edited version stays in version history.",
        confirm: "Regenerate section",
    },
};

export const KEEP_EDITS_COPY = {
    keepAllTitle: "Keep all edits",
    discardAllTitle: "Discard all edits",
    cancel: "Cancel",
    cardsLabel: "Your edits",
    listLabel: "Edits to choose",
    keepAllToggle: "Keep all",
    discardAllToggle: "Discard all",
    keepToggle: "Keep",
    discardToggle: "Discard",
    pillKeepingAll: "Keeping all",
    pillDiscardingAll: "Discarding all",
    titleOne: (header: string) => `You have edited ${header}`,
    titleSection: (section: string, header: string) => `You have edited ${section} in ${header}`,
    titleSeveral: (n: number) => `You have edited ${n} of these documents`,
    disclosureField: (n: number) => `Choose for each field (${n === 1 ? "1 edit" : `${n} edits`})`,
    disclosureDocument: (n: number) => `Choose for each document or field (${n === 1 ? "1 edit" : `${n} edits`})`,
    pillCustom: (kept: number, total: number) => `Custom: keeping ${kept} of ${total}`,
    spacingOnlyTag: "Spacing only",
    checkingToast: "Checking your edits...",
    editCount: (n: number) => (n === 1 ? "1 edit" : `${n} edits`),
};

export const TAG_LABELS: Record<EditRowTag, string> = {
    reverted_to_normal: "Reverted to normal",
    cleared: "Cleared",
    added: "Added by you",
    renamed: "Renamed",
};

export const LEAF_CONFIRM_COPY = {
    title: (name: string) => `Regenerate ${name}?`,
    body: "You edited this field. Regenerating replaces your text with a new version written from the recordings. Your edited version stays in version history.",
    cancel: "Cancel",
    confirm: "Discard edit and regenerate",
};

/**
 * EN-1492: stable ids for the flow's error toasts. react-toastify shows one toast per id, so a
 * repeated click while the toast is up does not stack copies of the same message.
 */
export const KEEP_EDITS_TOAST_IDS = {
    saveFailed: "keep-edits-save-failed",
    checkFailedKeptAll: "keep-edits-check-failed-kept-all",
    newerVersion: "keep-edits-newer-version",
    checking: "keep-edits-checking",
    regenerateUnavailable: "keep-edits-regenerate-unavailable",
    regenerateFlagSaveFailed: "keep-edits-regenerate-flag-save-failed",
    regenerateFailed: "keep-edits-regenerate-failed",
    alreadyWritten: "keep-edits-already-written",
    answerFailed: "keep-edits-answer-failed",
    stopDroppedKeptAll: "keep-edits-stop-dropped-kept-all",
    busy: "keep-edits-busy",
    leafDocumentChanged: "keep-edits-leaf-document-changed",
} as const;

/**
 * EN-1492 (GLaDOS HIGH): when the edits check fails after its retries (timeout, 5xx, network), generation
 * goes ahead keeping every edit (the D5 default when nobody answers) and the vet is told.
 */
export const CHECK_FAILED_KEPT_ALL_MESSAGE = "Could not check your edits, so all of them were kept.";

/** EN-1492 D22: a Stop & Generate answer that arrived after the document was written keeping all edits. */
export const ALREADY_WRITTEN_MESSAGE =
    "Your document was already written keeping all your edits. Use Retry to change that.";

/** EN-1492: a Generate, Retry or Regenerate dropped while another edits check or dialog has been up a while. */
export const BUSY_MESSAGE = "Finish the current step first.";

/** EN-1492: Stop & Generate pressed while another edits check or dialog is up, so this recording is not asked. */
export const STOP_DROPPED_KEPT_ALL_MESSAGE = "Your edits will all be kept for this recording.";

/** EN-1492: the single-field confirm closed because the editor switched to another version (D15). */
export const LEAF_DOCUMENT_CHANGED_MESSAGE = "The document changed, so nothing was regenerated.";

/** EN-1492: Stop & Generate could not save the open document first; the recording generates without that text. */
export const STOP_SAVE_FAILED_MESSAGE =
    "Your latest edits could not be saved, so the new document may not include them.";

/** EN-1492 D23: the Stop & Generate answer could not be sent, so generation keeps all edits. */
export const ANSWER_FAILED_MESSAGE =
    "Could not save your choice, so all your edits were kept. Use Retry to change that.";

/** EN-1492: Regenerate's error toasts, one stable id each (the flag-save toast fires from two failure branches). */
export const REGENERATE_ERROR_TOASTS = {
    flagSaveFailed: {
        message: "Unable to save regenerated field state. Please retry.",
        toastId: KEEP_EDITS_TOAST_IDS.regenerateFlagSaveFailed,
    },
    failed: {
        message: "Failed to regenerate section. Please try again.",
        toastId: KEEP_EDITS_TOAST_IDS.regenerateFailed,
    },
} as const;

export const showRegenerateErrorToast = (kind: keyof typeof REGENERATE_ERROR_TOASTS) => {
    const { message, toastId } = REGENERATE_ERROR_TOASTS[kind];
    toast.error(message, { toastId });
};
